import { useState, useEffect, useId } from 'react';
import { Box, Flex, Text, IconButton, Field } from '@chakra-ui/react';
import { PopoverRoot, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody } from '@chakra-ui/react';
import { HiFunnel } from 'react-icons/hi2';
import { InputField } from './input';

export interface FilterOption {
  label: string;
  value: string;
}

interface ColumnFilterPopoverProps {
  columnKey: string;
  currentValue?: string;
  options?: FilterOption[];
  onApply: (column: string, value: string) => void;
  onClear: (column: string) => void;
}

/**
 * @useFor per-column filtering inside a `<DataTable>` column header.
 *   Renders a funnel-icon trigger that opens a popover with a select
 *   (when `options` is set) or text input.
 *
 *   Examples in-tree: People list `Role` / `Status` columns,
 *   Onboarding jobs `Type` / `Status` columns.
 *
 *   For app-level filtering (date range, multi-field filters) use
 *   `<FilterButton>` in the toolbar instead — this is scoped to
 *   single-column value filters in the column header.
 */
export function ColumnFilterPopover({
  columnKey,
  currentValue,
  options,
  onApply,
  onClear,
}: ColumnFilterPopoverProps) {
  const [inputValue, setInputValue] = useState(currentValue ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const isActive = !!currentValue;
  const filterInputId = useId();

  // TODO(AAR-660): refactor away from prop-sync-to-state. Either lift state up,
  // use a `key` prop tied to currentValue, or derive inputValue directly from
  // currentValue. Disabled here to unblock CI; safe because the only state being
  // synchronized is a single primitive input value.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInputValue(currentValue ?? '');
  }, [currentValue]);

  return (
    <PopoverRoot
      positioning={{ placement: 'bottom-start' }}
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
    >
      <PopoverTrigger asChild>
        <IconButton
          aria-label={`Filter ${columnKey}`}
          variant="ghost"
          size="2xs"
          color={isActive ? 'brand.500' : 'text.tertiary'}
          _hover={{ color: isActive ? 'brand.600' : 'text.secondary' }}
          onClick={(e) => e.stopPropagation()}
        >
          <HiFunnel size={12} />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent
        width="220px"
        onClick={(e) => e.stopPropagation()}
      >
        <PopoverArrow />
        <PopoverBody p={3}>
          <Text fontSize="xs" fontWeight={600} mb={2} color="text.primary">
            Filter
          </Text>
          {options ? (
            <Flex direction="column" gap={1}>
              {options.map((opt) => (
                <Box
                  key={opt.value}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  cursor="pointer"
                  bg={currentValue === opt.value ? 'surface.hover' : 'transparent'}
                  fontWeight={currentValue === opt.value ? '600' : '400'}
                  _hover={{ bg: 'surface.hover' }}
                  onClick={() => {
                    if (currentValue === opt.value) {
                      onClear(columnKey);
                    } else {
                      onApply(columnKey, opt.value);
                    }
                    setIsOpen(false);
                  }}
                >
                  <Text fontSize="sm" color="text.primary">{opt.label}</Text>
                </Box>
              ))}
            </Flex>
          ) : (
            <Field.Root>
              <Field.Label htmlFor={filterInputId} srOnly>
                Filter by {columnKey}
              </Field.Label>
              <InputField
                id={filterInputId}
                size="sm"
                placeholder="Filter value..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onApply(columnKey, inputValue);
                    setIsOpen(false);
                  }
                }}
              />
            </Field.Root>
          )}
          <Flex mt={2} gap={2} justifyContent="flex-end">
            {isActive && (
              <Text
                as="button"
                fontSize="xs"
                color="text.secondary"
                cursor="pointer"
                _hover={{ color: 'text.primary' }}
                onClick={() => {
                  setInputValue('');
                  onClear(columnKey);
                  setIsOpen(false);
                }}
              >
                Clear
              </Text>
            )}
            {!options && (
              <Text
                as="button"
                fontSize="xs"
                color="brand.500"
                fontWeight={600}
                cursor="pointer"
                _hover={{ color: 'brand.600' }}
                onClick={() => {
                  onApply(columnKey, inputValue);
                  setIsOpen(false);
                }}
              >
                Apply
              </Text>
            )}
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  );
}
